import {
  action,
  comparer,
  computed,
  makeObservable,
  observable,
  reaction,
  runInAction,
} from "mobx";
import { MemCommonMentionKind } from "@mem-labs/common-editor";
import { matchPath } from "react-router";
import { LogoutState } from "@/components/sync/types";
import { AppSubStore, AppSubStoreArgs } from "@/store/types";
import { clientEnvModule } from "@/modules/client-env";
import { electronModule } from "@/modules/electron";
import { logger } from "@/modules/logger";
import type {
  MemAgentAccess,
  MemAgentTierOverride,
} from "@/store/space-account/observables/SpaceAccountObservable";
import type {
  FloatingAgentEvent,
  FloatingAgentPresence,
  FloatingAgentPresentationLocation,
} from "@/modules/electron/types";
import type { MDSComboboxMention } from "@/mds/components/forms";
import { getMentionId, getMentionLabel } from "@/mds/components/forms/combobox/mention-utils";
import { GatedFeature } from "@/store/feature-gates/types";
import { PaywallContext } from "@/domains/metrics";
import { convertPathToStringWithoutTid } from "@/store/navigation/helpers";
import {
  getMemAgentHoldToTalkKeyDisplay,
  type MemAgentHoldToTalkKeySetting,
} from "@/modules/mem-agent/hold-to-talk-key";
import {
  trackFloatingAgentProactiveTrialBanner,
  type FloatingAgentProactiveTrialBannerTrackingAction,
} from "@/modules/mem-agent/proactive-trial-banner-tracking";
import { trackFloatingAgentUpgradeButtonClicked } from "@/modules/mem-agent/upgrade-button-tracking";
import { ONBOARDING_STEPS_WITH_FLOATING_AGENT } from "./floating-agent-onboarding-steps";

const USER_ENABLED_STORAGE_KEY = "mem.floatingAgent.userEnabled";
const PRESENCE_STORAGE_KEY = "mem.floatingAgent.presence";
const MEM_AGENT_TIER_OVERRIDES_WITH_NATIVE_CHAT: ReadonlySet<MemAgentTierOverride> = new Set([
  "FREE",
  "PRO",
  "PROACTIVE",
]);
const MEM_AGENT_ACCESS_WITH_FLOATING: ReadonlySet<MemAgentAccess> = new Set([
  "chat-only",
  "floating-only",
  "floating-plus-chat",
]);
const PROACTIVE_TRIAL_BANNER_TRACKING_ACTION_BY_EVENT_KIND: Partial<
  Record<FloatingAgentEvent["kind"], FloatingAgentProactiveTrialBannerTrackingAction>
> = {
  "proactive-trial-banner-view": "view",
  "proactive-trial-banner-click": "click",
  "proactive-trial-banner-dismiss": "dismiss",
};
const DOCKED_VOICE_EVENT_KINDS: ReadonlySet<FloatingAgentEvent["kind"]> = new Set([
  "voice-hold-start",
  "voice-hold-confirmed",
  "voice-hold-end",
  "voice-open-mic-canceled",
  "voice-open-mic-recover",
  "voice-open-mic-cancel-expired",
  "voice-open-mic-ending-soon",
  "voice-open-mic-continued",
]);
/**
 * Titles are typed a character at a time and each edit re-resolves the
 * foreground notes, so the IPC push waits for the typing to settle.
 */
export const FOREGROUND_CONTEXT_PUSH_DEBOUNCE_MS = 300;
export type { FloatingAgentPresence } from "@/modules/electron/types";

const readStoredUserEnabled = (): boolean => {
  try {
    // Per-device preference; rollout/override gating is evaluated separately.
    return window.localStorage.getItem(USER_ENABLED_STORAGE_KEY) !== "false";
  } catch (_error) {
    return true;
  }
};

const readStoredPresence = (): FloatingAgentPresence => {
  try {
    const storedPresence = window.localStorage.getItem(PRESENCE_STORAGE_KEY);
    if (
      storedPresence === "always" ||
      storedPresence === "unread" ||
      storedPresence === "on_demand"
    ) {
      return storedPresence;
    }
  } catch (_error) {
    // Fall through to the default.
  }
  return "always";
};

/**
 * Evaluates floating-agent eligibility and publishes user presentation
 * preferences. Electron owns the exclusive docked/floating/hidden handoff
 * because it has authoritative main-window focus.
 */
export class AppStoreFloatingAgentStore extends AppSubStore {
  userEnabled: boolean = readStoredUserEnabled();
  presence: FloatingAgentPresence = readStoredPresence();
  presentationLocation: FloatingAgentPresentationLocation = "hidden";
  presentationTransitionId: number | null = null;
  /**
   * True while the in-window floating-agent host is showing reply preview
   * bubbles. The onboarding overlay uses this visual state to avoid overlapping
   * actual messages without treating the unread dot by itself as a collision.
   */
  hasVisibleReplyPreviews: boolean = false;

  /**
   * Monotonic open-panel request. The web widget reacts to changes and expands
   * its panel; desktop is driven over IPC in `requestOpenPanel`.
   */
  panelOpenRequestId: number = 0;
  headsUpPopoverToggleRequestId: number = 0;
  dockedEventRequestId: number = 0;

  /**
   * Mentions an in-app surface (multi-select "Chat") asks the composer to
   * pre-attach, paired with a monotonic id.
   *
   * The composer's chips are React state owned per pane, so a request has to
   * survive until a pane mounts and drains it. The id is what makes a repeat
   * request with the same notes take effect: the panel may already be open
   * with those exact chips dismissed, and comparing the array alone would
   * make the second click do nothing.
   */
  pendingComposerMentions: MDSComboboxMention[] = [];
  pendingComposerMentionsRequestId: number = 0;

  private dockedEventQueue: FloatingAgentEvent[] = [];

  private disposeConfigurationReaction: (() => void) | null = null;
  private disposeHoldToTalkKeyReaction: (() => void) | null = null;
  private disposeFloatingAgentEventListener: (() => void) | null = null;
  private disposeForegroundContextReaction: (() => void) | null = null;

  constructor(injectedDeps: AppSubStoreArgs) {
    super(injectedDeps);

    makeObservable<
      AppStoreFloatingAgentStore,
      | "disposeConfigurationReaction"
      | "disposeHoldToTalkKeyReaction"
      | "disposeFloatingAgentEventListener"
      | "disposeForegroundContextReaction"
      | "dockedEventQueue"
      | "pushConfiguration"
      | "pushHoldToTalkKey"
      | "refreshPermissionStatus"
      | "handleFloatingAgentEvent"
      | "pushForegroundContext"
      | "foregroundContextPushTimer"
    >(this, {
      userEnabled: observable,
      presence: observable,
      presentationLocation: observable,
      presentationTransitionId: observable,
      hasVisibleReplyPreviews: observable,
      panelOpenRequestId: observable,
      headsUpPopoverToggleRequestId: observable,
      dockedEventRequestId: observable,
      pendingComposerMentions: observable.shallow,
      pendingComposerMentionsRequestId: observable,
      dockedEventQueue: observable.shallow,
      requestOpenPanel: action,
      requestToggleHeadsUpPopover: action,
      requestAttachComposerMentions: action,
      consumeComposerMentions: action,
      adoptPendingComposerMentions: false,
      consumeDockedEvents: action,
      disposeConfigurationReaction: false,
      disposeHoldToTalkKeyReaction: false,
      disposeFloatingAgentEventListener: false,
      disposeForegroundContextReaction: false,
      isMemAgentAccessEnabled: computed,
      isMemAgentTierEnabled: computed,
      isHeadsUpFloatingWindowEnabled: computed,
      isOnboardingSurfaceAvailable: computed,
      isSuppressedForBootstrap: computed,
      isSuppressedForLogout: computed,
      isSuppressedForOnboarding: computed,
      shouldPrewarmStandaloneRuntime: computed,
      isEnabled: computed,
      isStandaloneRuntimeEnabled: computed,
      isDockedWidgetEnabled: computed,
      visibleNoteIds: computed,
      foregroundNotes: computed,
      setUserEnabled: action,
      setPresence: action,
      setHasVisibleReplyPreviews: action,
      initialize: false,
      pushConfiguration: false,
      pushHoldToTalkKey: false,
      refreshPermissionStatus: false,
      handleFloatingAgentEvent: action,
      pushForegroundContext: false,
      foregroundContextPushTimer: false,
      pendingVoiceRepository: false,
    });
  }

  /**
   * DB access stays behind the store layer; the widget page consumes the
   * repository through here rather than reaching into memDb directly.
   */
  get pendingVoiceRepository() {
    return this.store.memDb.floatingAgentPendingVoice;
  }

  get isMemAgentAccessEnabled(): boolean {
    const memAgentAccess = this.store.spaceAccounts.myPersonalSpaceAccount?.memAgentAccess;
    return memAgentAccess ? MEM_AGENT_ACCESS_WITH_FLOATING.has(memAgentAccess) : false;
  }

  get isMemAgentTierEnabled(): boolean {
    const tierOverride = this.store.spaceAccounts.myPersonalSpaceAccount?.memAgentTierOverride;
    if (tierOverride) {
      return MEM_AGENT_TIER_OVERRIDES_WITH_NATIVE_CHAT.has(tierOverride);
    }

    return this.isMemAgentAccessEnabled;
  }

  get isHeadsUpFloatingWindowEnabled(): boolean {
    return this.isOnboardingSurfaceAvailable;
  }

  /**
   * The onboarding prompt itself enables the user toggle, so this checks only
   * rollout, account access, tier access, auth mode, and whether a renderable
   * floating-agent surface exists for the current platform.
   */
  get isOnboardingSurfaceAvailable(): boolean {
    return (
      this.isMemAgentAccessEnabled && this.isMemAgentTierEnabled && this.store.auth.isStandardMode
    );
  }

  get isSuppressedForLogout(): boolean {
    return (
      this.store.sync.logoutState === LogoutState.LoggingOut ||
      this.store.sync.logoutState === LogoutState.LoggedOut
    );
  }

  /**
   * Keeps both the desktop window and web widget hidden until the logged-in
   * app has finished its startup sync and mounted follow-up initializers.
   */
  get isSuppressedForBootstrap(): boolean {
    return !this.store.readyState.data || this.store.sync.isBootstrapping;
  }

  /**
   * Keep the standalone window hidden until onboarding is authoritative and
   * reaches a step that explicitly uses the floating agent surface.
   */
  get isSuppressedForOnboarding(): boolean {
    if (!this.store.onboarding.hasInitialized) {
      return true;
    }

    const currentStepId = this.store.onboarding.currentStepId;
    return (
      this.store.onboarding.isActive &&
      (!currentStepId || !ONBOARDING_STEPS_WITH_FLOATING_AGENT.has(currentStepId))
    );
  }

  get isEnabled(): boolean {
    // The logout/auth terms push `prewarm: false` so the main process tears
    // down the widget window and summon key hook before auth cleanup finishes.
    return (
      this.isMemAgentAccessEnabled &&
      this.isMemAgentTierEnabled &&
      this.store.auth.isStandardMode &&
      !this.isSuppressedForBootstrap &&
      !this.isSuppressedForLogout &&
      !this.isSuppressedForOnboarding
    );
  }

  /**
   * Desktop can boot the standalone widget renderer before it is visible.
   * Eligibility excludes visibility gates so startup/onboarding still hide the
   * surface while the hidden BrowserWindow does its second renderer boot.
   */
  get shouldPrewarmStandaloneRuntime(): boolean {
    return (
      clientEnvModule.isDesktop() &&
      this.isMemAgentAccessEnabled &&
      this.isMemAgentTierEnabled &&
      this.store.auth.isStandardMode &&
      !this.isSuppressedForLogout
    );
  }

  get isStandaloneRuntimeEnabled(): boolean {
    return this.shouldPrewarmStandaloneRuntime;
  }

  get isDockedWidgetEnabled(): boolean {
    return clientEnvModule.isDesktop() && this.presentationLocation === "docked" && this.isEnabled;
  }

  /**
   * Ask the active agent surface to open its panel. Electron routes desktop
   * requests to the current presentation owner; web reacts locally.
   */
  requestOpenPanel() {
    if (clientEnvModule.isDesktop()) {
      void electronModule.floatingAgent.openPanel?.();
      return;
    }
    this.panelOpenRequestId += 1;
  }

  requestToggleHeadsUpPopover() {
    this.headsUpPopoverToggleRequestId += 1;
  }

  /**
   * Open the panel with `mentions` already attached as composer chips, ready
   * for the user to type their own message. Nothing is sent.
   *
   * The chips are ordinary removable ones, not `isExternal` foreground chips:
   * the user picked these notes explicitly, so dismissing one should stick
   * rather than be re-supplied by the foregrounded-note source.
   */
  requestAttachComposerMentions(mentions: MDSComboboxMention[]) {
    // Route by where the composer actually renders, not by platform. The
    // docked widget lives in this window, so it reads these observables
    // directly; the standalone desktop widget is a separate window with its
    // own store and must be reached through the main process. The IPC call
    // also summons that window's panel.
    if (clientEnvModule.isDesktop() && !this.isDockedWidgetEnabled) {
      void electronModule.floatingAgent.attachComposerMentions?.({
        mentions: mentions.map(mention => ({
          kind: mention.kind,
          id: getMentionId(mention),
          label: getMentionLabel(mention),
        })),
        requestId: ++this.pendingComposerMentionsRequestId,
      });
      return;
    }

    this.pendingComposerMentions = mentions;
    this.pendingComposerMentionsRequestId += 1;

    this.requestOpenPanel();
  }

  /**
   * Pulls any attach request the main process is holding for this widget
   * window and applies it locally, so the composer sees it through the same
   * observables the web path uses.
   *
   * Called when the desktop widget opens its panel — the request that summoned
   * it was sent to the main process, not to this window's store.
   */
  async adoptPendingComposerMentions(): Promise<void> {
    if (!clientEnvModule.isDesktop()) return;

    const request = await electronModule.floatingAgent.takeComposerMentions?.();
    if (!request || request.mentions.length === 0) return;

    const mentions = request.mentions.map(mention =>
      mention.kind === MemCommonMentionKind.Collection
        ? {
            kind: MemCommonMentionKind.Collection as const,
            isExternal: false,
            collection_id: mention.id,
            collection_title: mention.label,
          }
        : {
            kind: MemCommonMentionKind.Note as const,
            isExternal: false,
            note_id: mention.id,
            note_primary_label: mention.label,
          }
    );

    runInAction(() => {
      this.pendingComposerMentions = mentions;
      this.pendingComposerMentionsRequestId = request.requestId;
    });
  }

  /**
   * Clears a request once a composer has attached it, so reopening the panel
   * later does not replay a stale selection.
   */
  consumeComposerMentions() {
    if (this.pendingComposerMentions.length === 0) return;

    this.pendingComposerMentions = [];
  }

  consumeDockedEvents(): FloatingAgentEvent[] {
    return this.dockedEventQueue.splice(0);
  }

  setUserEnabled(enabled: boolean) {
    this.userEnabled = enabled;
    try {
      window.localStorage.setItem(USER_ENABLED_STORAGE_KEY, String(enabled));
    } catch (_error) {
      // Persistence is best-effort; modern visibility is controlled by presence.
    }
  }

  setPresence(presence: FloatingAgentPresence) {
    this.presence = presence;
    try {
      window.localStorage.setItem(PRESENCE_STORAGE_KEY, presence);
    } catch (_error) {
      // Persistence is best-effort; the in-memory preference still applies.
    }
  }

  setHasVisibleReplyPreviews(hasVisibleReplyPreviews: boolean) {
    this.hasVisibleReplyPreviews = hasVisibleReplyPreviews;
  }

  initialize() {
    if (!clientEnvModule.isDesktop()) return;

    // Install the listener before the fire-immediate configuration push:
    // Electron replies synchronously with the initial presentation owner.
    this.disposeFloatingAgentEventListener?.();
    this.disposeFloatingAgentEventListener = electronModule.floatingAgent.onEvent(
      this.handleFloatingAgentEvent
    );

    this.disposeConfigurationReaction?.();
    this.disposeConfigurationReaction = reaction(
      () => ({
        enabled: this.isEnabled,
        prewarm: this.shouldPrewarmStandaloneRuntime,
        presence: this.presence,
      }),
      configuration => {
        this.pushConfiguration(configuration);
        if (configuration.enabled) void this.refreshPermissionStatus();
      },
      { fireImmediately: true }
    );

    this.disposeHoldToTalkKeyReaction?.();
    this.pushHoldToTalkKey({
      key: this.store.spaceAccountSettings.memAgent.holdToTalkKey,
      isEnabled: this.store.spaceAccountSettings.memAgent.holdToTalkIsEnabled,
    });
    this.disposeHoldToTalkKeyReaction = reaction(
      () => ({
        key: this.store.spaceAccountSettings.memAgent.holdToTalkKey,
        isEnabled: this.store.spaceAccountSettings.memAgent.holdToTalkIsEnabled,
      }),
      shortcut => this.pushHoldToTalkKey(shortcut)
    );

    // Publish the main window's visible-note context for the widget's
    // implicit mentions. Tracks resolved notes, not just ids, so a rename
    // republishes; structural comparison keeps an unrelated recompute of the
    // freshly built array from spending an IPC round-trip.
    this.disposeForegroundContextReaction?.();
    const disposeReaction = reaction(
      () => (this.isStandaloneRuntimeEnabled ? this.foregroundNotes : null),
      notes => {
        if (notes === null) return;
        this.pushForegroundContext(notes);
      },
      { fireImmediately: true, equals: comparer.structural }
    );
    this.disposeForegroundContextReaction = () => {
      disposeReaction();
      if (this.foregroundContextPushTimer !== null) {
        clearTimeout(this.foregroundContextPushTimer);
        this.foregroundContextPushTimer = null;
      }
    };
  }

  /**
   * Note ids open in visible panes' active tabs (deduplicated — split view
   * can show the same note twice), as a stable joined key so consumers (the
   * desktop IPC publisher, the web widget's reaction) only fire on real
   * changes.
   */
  get visibleNoteIds(): string {
    const navigationTabs = this.store.navigation.tabs;
    const noteIds = new Set<string>();

    for (const pane of navigationTabs.panesMap.values()) {
      if (!pane.activeTabId) continue;

      const path = navigationTabs.tabsMap.get(pane.activeTabId)?.path;
      if (!path) continue;

      const noteMatch = matchPath("/notes/:noteId", convertPathToStringWithoutTid(path));
      if (noteMatch?.params.noteId) noteIds.add(noteMatch.params.noteId);
    }

    return [...noteIds].join(",");
  }

  /**
   * The foregrounded notes with display labels, as a live read: `title` is a
   * computed on the note observable, so renaming a note the user is already
   * looking at re-resolves its label. Snapshotting labels against note ids
   * instead would leave a new note's chip reading "Untitled Note" forever.
   *
   * `notes.get` returns undefined until the note's observable loads, and an
   * unresolved note is skipped rather than emitted with an empty label.
   */
  get foregroundNotes(): Array<{ noteId: string; label: string }> {
    const joinedNoteIds = this.visibleNoteIds;
    const noteIds = joinedNoteIds.length > 0 ? joinedNoteIds.split(",") : [];

    return noteIds
      .map(noteId => {
        const noteObservable = this.store.notes.get(noteId);
        return noteObservable ? { noteId, label: noteObservable.title } : null;
      })
      .filter((note): note is { noteId: string; label: string } => note !== null);
  }

  private foregroundContextPushTimer: ReturnType<typeof setTimeout> | null = null;

  /**
   * Trailing debounce, so a title typed a character at a time costs one IPC
   * push rather than one per keystroke. MobX's `reaction` `delay` is a
   * leading-edge throttle and would let a burst through in pieces.
   */
  private pushForegroundContext(notes: Array<{ noteId: string; label: string }>): void {
    if (this.foregroundContextPushTimer !== null) {
      clearTimeout(this.foregroundContextPushTimer);
    }

    this.foregroundContextPushTimer = setTimeout(() => {
      this.foregroundContextPushTimer = null;
      void electronModule.floatingAgent.setForegroundContext({ notes }).catch((error: unknown) => {
        logger.warn({
          message: "[AppStoreFloatingAgentStore] failed to push foreground context",
          info: { error: String(error) },
        });
      });
    }, FOREGROUND_CONTEXT_PUSH_DEBOUNCE_MS);
  }

  private pushConfiguration(configuration: {
    enabled: boolean;
    prewarm: boolean;
    presence: FloatingAgentPresence;
  }) {
    void electronModule.floatingAgent.configure(configuration).catch((error: unknown) => {
      logger.warn({
        message: "[AppStoreFloatingAgentStore] failed to push configuration",
        info: { error: String(error) },
      });
    });
  }

  private pushHoldToTalkKey({
    key,
    isEnabled,
  }: {
    key: MemAgentHoldToTalkKeySetting;
    isEnabled: boolean;
  }) {
    const activeKey = isEnabled ? key : null;
    const display = activeKey ? getMemAgentHoldToTalkKeyDisplay(activeKey) : null;
    void electronModule.floatingAgent
      .setHoldToTalkKey({
        key: activeKey,
        tooltipKey: display?.tooltipKey ?? "",
        sentencePrefix: display?.sentencePrefix ?? "",
      })
      .catch((error: unknown) => {
        logger.warn({
          message: "[AppStoreFloatingAgentStore] failed to push hold-to-talk key",
          info: { error: String(error) },
        });
      });
  }

  /**
   * The summon/PTT key hook needs macOS Accessibility trust. Onboarding owns
   * the actual ask; here we only refresh the status so the widget's
   * "voice needs permission" notice knows whether to show.
   */
  private async refreshPermissionStatus() {
    try {
      await this.store.systemPermissions.syncPermissions();
    } catch (error) {
      logger.warn({
        message: "[AppStoreFloatingAgentStore] permission status refresh failed",
        info: { error: String(error) },
      });
    }
  }

  private handleFloatingAgentEvent = (event: FloatingAgentEvent) => {
    if (event.kind === "presentation-location-changed") {
      const location = event.presentationLocation;
      if (location === "docked" || location === "floating" || location === "hidden") {
        this.presentationLocation = location;
        this.presentationTransitionId =
          typeof event.presentationTransitionId === "number" &&
          Number.isSafeInteger(event.presentationTransitionId)
            ? event.presentationTransitionId
            : null;
      }
      return;
    }

    if (
      this.presentationLocation === "docked" &&
      (event.kind === "summon" ||
        event.kind === "onboarding-nudge" ||
        DOCKED_VOICE_EVENT_KINDS.has(event.kind))
    ) {
      this.dockedEventQueue.push(event);
      this.dockedEventRequestId += 1;
      return;
    }

    const proactiveTrialBannerAction =
      PROACTIVE_TRIAL_BANNER_TRACKING_ACTION_BY_EVENT_KIND[event.kind];
    if (proactiveTrialBannerAction) {
      trackFloatingAgentProactiveTrialBanner({
        action: proactiveTrialBannerAction,
        store: this.store,
      });
      return;
    }

    if (event.kind === "first-message-onboarding-completed") {
      const perDeviceOnboardingInfo = this.store.platformInfo.perDeviceOnboardingInfo;
      const completionTrigger = event.firstMessageOnboardingCompletionTrigger ?? "message-sent";
      const intentOnboardingInfo = this.store.platformInfo.onboardingInfo;
      const isIntentQuickThoughtCapture =
        intentOnboardingInfo.onboardingFlowVersion === "intent_v1" &&
        intentOnboardingInfo.onboardingIntent === "quick_thoughts";
      const shouldCompleteNewUserOnboarding =
        !isIntentQuickThoughtCapture || completionTrigger === "message-sent";

      if (
        shouldCompleteNewUserOnboarding &&
        perDeviceOnboardingInfo.memAgentOnboardingIsRelevant &&
        !perDeviceOnboardingInfo.hasCompletedMemAgentShortcutOnboarding
      ) {
        perDeviceOnboardingInfo.completeMemAgentShortcutOnboarding();
      }
      if (
        shouldCompleteNewUserOnboarding &&
        perDeviceOnboardingInfo.memAgentOnboardingIsRelevant &&
        !perDeviceOnboardingInfo.hasCompletedMemAgentFirstMessageOnboarding
      ) {
        perDeviceOnboardingInfo.completeMemAgentFirstMessageOnboarding();
      }
      if (
        perDeviceOnboardingInfo.existingUserMemAgentOnboardingIsRelevant &&
        !this.store.platformInfo.onboardingInfo.hasCompletedTask(
          "existing-user-mem-agent-first-message"
        )
      ) {
        void this.store.platformInfo.onboardingInfo.completeOnboardingTasks([
          "existing-user-mem-agent-first-message",
        ]);
      }
      return;
    }

    // The widget window has no modal layer, so its Upgrade button routes here.
    if (event.kind === "proactive-upsell") {
      trackFloatingAgentUpgradeButtonClicked({ store: this.store });
      this.store.subscriptions.openProactiveUpsell();
      return;
    }

    if (event.kind !== "native-chat-limit-upsell") return;
    if (typeof event.used !== "number" || typeof event.limit !== "number") return;

    this.store.featureGates.openUsageLimitUpsell({
      used: event.used,
      limit: event.limit,
      feature: GatedFeature.NativeChatMessageSend,
      context: PaywallContext.NativeChatMessageSend,
    });
  };
}
